import { Clock, Lock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useLanguage } from '@/contexts/LanguageContext';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import {
  isApplicationAwaitingUpgrade,
  isApplicationBillingPipelineError,
  isApplicationReadyToProcess,
  canProcessPendingApplications,
} from '@/lib/pendingProcessing';
import type { Application } from '@/types';

interface Props {
  app: Pick<Application, 'state' | 'lastFailureReason'>;
  className?: string;
}

export default function PendingBillingBadge({ app, className }: Props) {
  const { t } = useLanguage();
  const { data: billingStatus } = useBillingStatus();
  const hasProcessingCredits = canProcessPendingApplications(billingStatus);
  const awaitingUpgrade = isApplicationAwaitingUpgrade(app, hasProcessingCredits);
  const readyToProcess = isApplicationReadyToProcess(app, hasProcessingCredits);
  const billingPipelineError = isApplicationBillingPipelineError(app);

  if (!awaitingUpgrade && !readyToProcess && !billingPipelineError) {
    return null;
  }

  const showAsAwaitingUpgrade = awaitingUpgrade || billingPipelineError;

  const label = showAsAwaitingUpgrade
    ? t('application.pendingUpgrade.badge')
    : readyToProcess
      ? t('application.readyToProcess.badge')
      : t('application.pendingBilling.badge');
  const tooltip = showAsAwaitingUpgrade
    ? t('application.pendingUpgrade.tooltip')
    : readyToProcess
      ? t('application.readyToProcess.tooltip')
      : t('application.pendingBilling.tooltip');
  const Icon = showAsAwaitingUpgrade ? Lock : Clock;
  const badgeClass = readyToProcess && !showAsAwaitingUpgrade
    ? 'border border-border bg-muted text-muted-foreground'
    : 'border border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-300';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap ${badgeClass} ${className ?? ''}`}
        >
          <Icon className="h-3 w-3 shrink-0" />
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
